import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * Access Code Management Component
 * CRUD operations for tool access codes
 *
 * IMPORTANT: Modal pattern - see /.claude/modal-pattern.md for documentation.
 * Do not extract Modal into a separate component - it breaks CSS animations.
 */
const AccessCodeManagement = () => {
  const [codes, setCodes] = useState([]);
  const [publicCodeId, setPublicCodeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    credit_amount: 0.20,
    code_type: 'daily',
    access_level: 'private',
    markup_percentage: 5
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await fetch('/api/admin/tools/access-codes', {
        credentials: 'include'
      });
      const data = await response.json();
      setCodes(data.codes);
      setPublicCodeId(data.publicCodeId);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch access codes');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingCode
        ? `/api/admin/tools/access-codes/${editingCode.id}`
        : '/api/admin/tools/access-codes';

      const response = await fetch(url, {
        method: editingCode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowModal(false);
        resetForm();
        fetchCodes();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save access code');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this access code?')) return;

    try {
      const response = await fetch(`/api/admin/tools/access-codes/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.error) {
        alert(data.error);
        return;
      }
      fetchCodes();
    } catch (err) {
      alert('Failed to delete access code');
    }
  };

  const handleSetPublic = async (id) => {
    try {
      const response = await fetch(`/api/admin/tools/access-codes/${id}/set-public`, {
        method: 'PUT',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.error) {
        alert(data.error);
        return;
      }
      fetchCodes();
    } catch (err) {
      alert('Failed to set public code');
    }
  };

  const handleDisableTool = async () => {
    if (!window.confirm('Are you sure you want to disable the tool?')) return;

    try {
      await fetch('/api/admin/tools/public-code', {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchCodes();
    } catch (err) {
      alert('Failed to disable tool');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      credit_amount: 0.20,
      code_type: 'daily',
      access_level: 'private',
      markup_percentage: 5
    });
    setEditingCode(null);
  };

  const openEditModal = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      name: code.name || '',
      credit_amount: code.credit_amount,
      code_type: code.code_type,
      access_level: code.access_level,
      markup_percentage: code.markup_percentage,
      is_active: code.is_active
    });
    setShowModal(true);
  };

  if (loading) {
    return (
      <AdminToolsLayout>
        <div className="text-center py-4"><Spinner animation="border" /></div>
      </AdminToolsLayout>
    );
  }

  return (
    <AdminToolsLayout>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Access Codes</h5>
          <div>
            {publicCodeId && (
              <Button variant="outline-danger" size="sm" className="me-2" onClick={handleDisableTool}>
                Disable Tool
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={() => { resetForm(); setShowModal(true); }}>
              Create Code
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Table responsive hover>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Credits</th>
                <th>Markup</th>
                <th>Status</th>
                <th>Articles</th>
                <th>Today</th>
                <th>Total</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code.id}>
                  <td>
                    <code>{code.code}</code>
                    {code.isPublicCode && (
                      <Badge bg="success" className="ms-2">PUBLIC</Badge>
                    )}
                  </td>
                  <td>{code.name || '-'}</td>
                  <td>
                    <Badge bg={code.code_type === 'daily' ? 'info' : 'secondary'}>
                      {code.code_type}
                    </Badge>
                  </td>
                  <td>
                    ${parseFloat(code.credit_amount).toFixed(2)}
                    {code.code_type === 'daily' && code.remainingToday !== undefined && (
                      <div style={{ fontSize: '0.75rem' }} className="text-muted">
                        ${code.remainingToday?.toFixed(4)} left today
                      </div>
                    )}
                    {code.code_type === 'one_time' && code.remainingTotal !== undefined && (
                      <div style={{ fontSize: '0.75rem' }} className="text-muted">
                        ${code.remainingTotal?.toFixed(4)} remaining
                      </div>
                    )}
                  </td>
                  <td>{code.markup_percentage}%</td>
                  <td>
                    <Badge bg={code.is_active ? 'success' : 'danger'}>
                      {code.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td>{code.articleCount || 0}</td>
                  <td>
                    ${(code.todaySpend || 0).toFixed(4)}
                    {code.todayRequests > 0 && (
                      <div style={{ fontSize: '0.75rem' }} className="text-muted">
                        {code.todayRequests} req
                      </div>
                    )}
                  </td>
                  <td>${parseFloat(code.total_amount_used || 0).toFixed(4)}</td>
                  <td>
                    <Link to={`/admin/tools/spend-report?code=${code.id}`} className="btn btn-outline-info btn-sm me-1" title="View daily spend">
                      Spend
                    </Link>
                    <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openEditModal(code)}>
                      Edit
                    </Button>
                    {!code.isPublicCode && code.code_type === 'daily' && (
                      <Button variant="outline-success" size="sm" className="me-1" onClick={() => handleSetPublic(code.id)}>
                        Set Public
                      </Button>
                    )}
                    {!code.isPublicCode && (
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(code.id)}>
                        Delete
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Create/Edit Modal - DO NOT extract into separate component, see /.claude/modal-pattern.md */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingCode ? 'Edit Access Code' : 'Create Access Code'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Code</Form.Label>
              <Form.Control
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                disabled={!!editingCode}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Name (optional)</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Credit Amount ($)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={formData.credit_amount}
                onChange={(e) => setFormData({ ...formData, credit_amount: parseFloat(e.target.value) })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={formData.code_type}
                onChange={(e) => setFormData({ ...formData, code_type: e.target.value })}
              >
                <option value="daily">Daily (resets each day)</option>
                <option value="one_time">One-time (until exhausted)</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Markup (%)</Form.Label>
              <Form.Control
                type="number"
                step="0.5"
                value={formData.markup_percentage}
                onChange={(e) => setFormData({ ...formData, markup_percentage: parseFloat(e.target.value) })}
                disabled={formData.access_level === 'public'}
              />
              <Form.Text className="text-muted">
                Public codes always have 0% markup
              </Form.Text>
            </Form.Group>

            {editingCode && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  checked={!!formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingCode ? 'Save Changes' : 'Create Code'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </AdminToolsLayout>
  );
};

export default AccessCodeManagement;
